import React from 'react';
import { CreditCard, Banknote, ArrowLeft, MapPin, Tag, X, Check, Gift, Store, Truck, Clock, Plus, ChevronRight, Trash2, Phone, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { formatPrice } from '../../lib/utils';
import StampCard from '../StampCard';

const DeliveryAddressCard = ({
  cart,
  checkingDelivery,
  city,
  deliveryFee,
  deliveryInfo,
  houseNumber,
  name,
  notes,
  orderType,
  postalCode,
  ringBellName,
  setCity,
  setHouseNumber,
  setNotes,
  setPostalCode,
  setRingBellName,
  setStreet,
  street,
  t,
}) => (
  <>
              {orderType === 'delivery' && (
              <Card className="bg-[#141417] border-[#27272A]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-[#FFC107]" />
                    {t('checkout.deliveryAddress')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="street">{t('checkout.street')} *</Label>
                      <Input
                        id="street"
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        required
                        placeholder={t('checkout.streetPlaceholder')}
                        className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                        data-testid="checkout-street"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="houseNumber">{t('checkout.houseNumber')} *</Label>
                      <Input
                        id="houseNumber"
                        value={houseNumber}
                        onChange={(e) => setHouseNumber(e.target.value)}
                        required
                        placeholder={t('checkout.houseNumberPlaceholder')}
                        className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                        data-testid="checkout-house-number"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postalCode">{t('checkout.postalCode')} *</Label>
                      <Input
                        id="postalCode"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        required
                        placeholder={t('checkout.postalCodePlaceholder')}
                        className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                        data-testid="checkout-postal-code"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">{t('checkout.city')} *</Label>
                      <Input
                        id="city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                        placeholder={t('checkout.cityPlaceholder')}
                        className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                        data-testid="checkout-city"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ringBellName">{t('checkout.ringBellName')} *</Label>
                    <Input
                      id="ringBellName"
                      value={ringBellName}
                      onChange={(e) => setRingBellName(e.target.value)}
                      required
                      placeholder={t('checkout.ringBellNamePlaceholder')}
                      className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                      data-testid="checkout-ring-bell-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">{t('checkout.notes')}</Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder={t('checkout.notesPlaceholder')}
                      className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                      data-testid="checkout-notes"
                    />
                  </div>
                  {deliveryInfo && !deliveryInfo.pickup_only && (
                    <div className="space-y-2">
                      <div className={`p-3 rounded-lg border ${
                        deliveryInfo.min_order_met === false ? 'bg-red-500/10 border-red-500/30' :
                        deliveryInfo.fee === 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-amber-500/10 border-amber-500/30'
                      }`} data-testid="delivery-fee-info">
                        <div className="flex items-center gap-2">
                          <Truck className="h-4 w-4 shrink-0" style={{ color: deliveryInfo.min_order_met === false ? '#ef4444' : deliveryInfo.fee === 0 ? '#22c55e' : '#f59e0b' }} />
                          <span className="text-sm font-medium" style={{ color: deliveryInfo.min_order_met === false ? '#ef4444' : deliveryInfo.fee === 0 ? '#22c55e' : '#f59e0b' }}>
                            {deliveryInfo.min_order_met === false
                              ? `${t('delivery.minOrder', 'Min. order')} CHF ${deliveryInfo.min_order} (${deliveryInfo.distance_km} km)`
                              : deliveryInfo.fee === 0
                              ? `${t('delivery.free', 'Free delivery')} (${deliveryInfo.distance_km} km)`
                              : `${t('cart.deliveryFee')}: CHF ${deliveryInfo.fee} (${deliveryInfo.distance_km} km)`
                            }
                          </span>
                        </div>
                      </div>
                      {deliveryInfo.zone === 2 && (
                        <div className="p-3 rounded-lg border bg-blue-500/10 border-blue-500/30" data-testid="zone2-call-notice">
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 shrink-0 text-blue-400" />
                            <span className="text-sm font-medium text-blue-400">
                              {t('delivery.zone2CallNotice', 'Please call us at 091 923 14 28 for order confirmation and delivery time')}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {checkingDelivery && (
                    <p className="text-sm text-zinc-500 animate-pulse">{t('delivery.checking', 'Checking delivery zone...')}</p>
                  )}
                </CardContent>
              </Card>
              )}
  </>
);

export default DeliveryAddressCard;
